import React, { useRef, useEffect, useState } from 'react';
import { Check, ArrowRight } from 'lucide-react';

export interface FeatureShowcaseProps {
  id: string;
  eyebrow: string;
  title: string;
  highlight: string;
  description: string;
  capabilities: string[];
  visual: React.ReactNode;
  stats?: { value: string; label: string }[];
  ctaText?: string;
  ctaAction?: () => void;
  reversed?: boolean;
  bgColor?: string;
}

const FeatureShowcase: React.FC<FeatureShowcaseProps> = ({
  eyebrow,
  title,
  highlight,
  description,
  capabilities,
  visual,
  stats,
  ctaText = "Explore Feature",
  ctaAction,
  reversed = false,
  bgColor = "bg-white"
}) => {
  const sectionRef = useRef<HTMLElement>(null);
  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.unobserve(entry.target);
        }
      },
      { threshold: 0.2, rootMargin: "-50px" }
    );

    if (sectionRef.current) {
      observer.observe(sectionRef.current);
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      ref={sectionRef}
      className={`relative py-20 lg:py-32 overflow-hidden ${bgColor}`}
    >
      <div className="container-main">
        <div className={`grid md:grid-cols-2 gap-8 lg:gap-12 xl:gap-20 items-center ${reversed ? 'md:flex-row-reverse' : ''}`}>
          {/* Content Side */}
            <div className={`space-y-8 ${reversed ? 'md:order-2' : 'md:order-1'} ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}>
            {/* Eyebrow */}
            <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-50 rounded-full">
              <span className="w-2 h-2 bg-accent-orange rounded-full animate-pulse" />
              <span className="text-sm font-semibold text-primary-600 uppercase tracking-wider">
                {eyebrow}
              </span>
            </div>

            {/* Title */}
            <div className="space-y-4">
              <h2 className="text-4xl lg:text-5xl xl:text-6xl font-bold font-heading text-navy-950 leading-tight">
                {title}{" "}
                <span className="text-primary-600">{highlight}</span>
              </h2>
              <p className="text-xl text-gray-600 leading-relaxed max-w-xl">
                {description}
              </p>
            </div>

            {/* Capabilities */}
            <div className="grid sm:grid-cols-2 gap-3">
              {capabilities.map((capability, idx) => (
                <div
                  key={idx}
                  className={`group flex items-start gap-3 p-3.5 bg-gray-50/50 rounded-lg border border-gray-100 transition-all duration-300 hover:bg-white hover:border-primary-200 hover:translate-x-1 ${isVisible ? 'animate-fade-in-up' : 'opacity-0'}`}
                  style={{ animationDelay: `${(idx + 1) * 100}ms` }}
                >
                  <div className="w-5 h-5 bg-accent-green/10 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 transition-transform duration-300 group-hover:scale-110">
                    <Check className="w-3 h-3 text-accent-green" />
                  </div>
                  <span className="text-gray-700 font-medium text-sm leading-relaxed">{capability}</span>
                </div>
              ))}
            </div>

            {/* Stats */}
            {stats && (
              <div className="flex flex-wrap gap-8 pt-4">
                {stats.map((stat, idx) => (
                  <div key={idx} className="text-center">
                    <div className="text-3xl lg:text-4xl font-bold text-navy-950 font-heading">
                      {stat.value}
                    </div>
                    <div className="text-sm text-gray-500 uppercase tracking-wide">
                      {stat.label}
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* CTA */}
            <div className="pt-4">
              <button
                onClick={ctaAction}
                className="group inline-flex items-center gap-3 text-lg font-semibold text-primary-600 hover:text-primary-700 transition-colors"
              >
                {ctaText}
                <ArrowRight className="w-5 h-5 transition-transform group-hover:translate-x-1" />
              </button>
            </div>
          </div>

          {/* Visual Side */}
          <div className={`relative z-10 ${reversed ? 'md:order-1' : 'md:order-2'} ${isVisible ? 'animate-fade-in-up animation-delay-200' : 'opacity-0'}`}>
            <div className="relative rounded-2xl overflow-hidden shadow-sm bg-gradient-to-br from-gray-50 to-gray-100 border border-gray-200 z-10">
              {visual}
            </div>
            
            {/* Decorative elements */}
            <div className="absolute -z-10 -top-10 -right-10 w-72 h-72 bg-primary-100/50 rounded-full blur-3xl" />
            <div className="absolute -z-10 -bottom-10 -left-10 w-72 h-72 bg-accent-orange/10 rounded-full blur-3xl" />
          </div>
        </div>
      </div>
    </section>
  );
};

export default FeatureShowcase;
